import type { TvShow } from '../content/tv';
import type { SfxName } from '../content/types';
import type { Random } from '../core/Random';
import { clear, h, layer } from './dom';

/** A wood-paneled CRT playing one of Lucky Acres' finest programs. */
export class TvScreen {
  readonly el: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private open = false;
  private timer: number | null = null;
  private watched = 0;
  private onDone: ((showsWatched: number, murray: boolean) => void) | null = null;
  private sawMurray = false;

  constructor(
    parent: HTMLElement,
    private readonly hooks: {
      rng: Random;
      pick: () => TvShow;
      sfx(n: SfxName): void;
      onOpen(): void;
      onClose(): void;
    },
  ) {
    this.el = layer();
    this.panel = h('div', {
      class: 'tv',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Television' },
    });
    this.el.append(this.panel);
    parent.append(this.el);
    window.addEventListener(
      'keydown',
      (e) => {
        if (!this.open) return;
        e.stopImmediatePropagation();
        if (e.key === 'Escape' || e.code === 'KeyE') {
          e.preventDefault();
          this.close();
        } else if (e.code === 'Space' || e.key === 'ArrowRight') {
          e.preventDefault();
          this.play(this.hooks.pick());
        }
      },
      true,
    );
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(onDone: (showsWatched: number, murray: boolean) => void): void {
    this.onDone = onDone;
    this.watched = 0;
    this.sawMurray = false;
    if (!this.open) this.hooks.onOpen();
    this.open = true;
    this.el.hidden = false;
    this.play(this.hooks.pick());
  }

  private play(show: TvShow): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.watched++;
    if (show.id === 'murray') this.sawMurray = true;
    clear(this.panel);
    const caption = h('div', { class: 'tv-caption' });
    const icon = h('div', { class: 'tv-icon', text: show.icon });
    this.panel.append(
      h(
        'div',
        { class: 'tv-screen' },
        h('div', { class: 'tv-channel', text: show.channel }),
        h('div', { class: 'tv-title', text: show.title }),
        icon,
        caption,
      ),
      h(
        'div',
        { class: 'tv-controls' },
        h('button', {
          class: 'tape',
          text: ':tv: Change channel (Space)',
          on: { click: () => this.play(this.hooks.pick()) },
        }),
        h('button', { class: 'tape primary', text: 'Turn it off (E)', on: { click: () => this.close() } }),
      ),
    );
    const lines = show.scenes(this.hooks.rng);
    let i = 0;
    caption.textContent = lines[0] ?? '';
    this.hooks.sfx('door');
    this.timer = window.setInterval(() => {
      i++;
      if (i >= lines.length) {
        if (this.timer !== null) window.clearInterval(this.timer);
        this.timer = null;
        if (show.id === 'murray') this.hooks.sfx('crowd');
        return;
      }
      caption.textContent = lines[i] ?? '';
      icon.style.transform = `rotate(${(Math.random() - 0.5) * 16}deg) scale(${1 + Math.random() * 0.15})`;
      if (show.id === 'murray' && i === lines.length - 1) this.hooks.sfx('crowd');
    }, 2300);
  }

  close(): void {
    if (!this.open) return;
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    this.open = false;
    this.el.hidden = true;
    const cb = this.onDone;
    this.onDone = null;
    this.hooks.onClose();
    cb?.(this.watched, this.sawMurray);
  }
}
